import { loadBudgetAccountMapping } from '@staiter/config';
import * as m from '@staiter/i18n';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Decimal } from 'decimal.js-light';
import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { PageHeader } from '~/components/data';
import { Button, DatePicker, Input, Select } from '~/components/primitives';
import { type BudgetCreateBody, createBudget } from '~/lib/api/budgets';
import { fetchProjects } from '~/lib/api/projects';
import shared from './_auth._shared.module.css';
import styles from './_auth.budgets.new.module.css';

/**
 * S16-FE.2 — Budget create wizard (3-step, config-driven account picker).
 */

export const Route = createFileRoute('/_auth/budgets/new')({ component: BudgetNew });

type LineForm = {
  key: string;
  account_code: string;
  account_name_ru: string;
  direction: 'revenue' | 'cost';
  plan_amount: string;
  period_split: 'quarterly' | 'monthly' | 'lump_sum';
};

const mapping = loadBudgetAccountMapping().mapping;

function makeLineFromMapping(code: string, idx: number): LineForm {
  const row = mapping.find((r) => r.account_code === code)!;
  return {
    key: `line-${idx}-${Date.now()}`,
    account_code: row.account_code,
    account_name_ru: row.account_name_ru,
    direction: row.direction,
    plan_amount: '0',
    period_split: 'quarterly',
  };
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}
function plusMonths(iso: string, months: number): string {
  const t = new Date(iso);
  t.setUTCMonth(t.getUTCMonth() + months);
  return t.toISOString().slice(0, 10);
}

/** FE-124 Sprint Q+: smart defaults — следующий квартал (Jan-Mar / Apr-Jun / ...). */
function nextQuarterRange(): { from: string; to: string } {
  const now = new Date();
  const currentQuarter = Math.floor(now.getUTCMonth() / 3); // 0..3
  const nextQuarterStart = new Date(Date.UTC(now.getUTCFullYear(), (currentQuarter + 1) * 3, 1));
  // Если уже Q4 — следующий квартал в новом году.
  if (currentQuarter === 3) {
    nextQuarterStart.setUTCFullYear(now.getUTCFullYear() + 1);
    nextQuarterStart.setUTCMonth(0);
  }
  const nextQuarterEnd = new Date(nextQuarterStart);
  nextQuarterEnd.setUTCMonth(nextQuarterEnd.getUTCMonth() + 3);
  nextQuarterEnd.setUTCDate(nextQuarterEnd.getUTCDate() - 1);
  return {
    from: nextQuarterStart.toISOString().slice(0, 10),
    to: nextQuarterEnd.toISOString().slice(0, 10),
  };
}

// FE-121 Sprint P+: budget wizard step labels.
function StepIndicator({ current, total }: { current: number; total: number }): JSX.Element {
  const STEP_LABELS = [
    m.budget_wizard_step1_label(),
    m.budget_wizard_step2_label(),
    m.budget_wizard_step3_label(),
  ];
  return (
    <div className={shared.wizardSteps}>
      {Array.from({ length: total }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: wizard step index = stable position
        <span
          key={`wizard-step-${i}`}
          className={i + 1 === current ? shared.wizardStepChipActive : shared.wizardStepChip}
        >
          {i + 1}. {STEP_LABELS[i] ?? `${i + 1} / ${total}`}
        </span>
      ))}
    </div>
  );
}

function BudgetNew(): JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [projectTag, setProjectTag] = useState('');
  // «Проект» — freeform tag, но даём автодополнение реальными названиями проектов
  // (datalist; свободный ввод сохраняется). Проектов немного — грузим список целиком.
  const projectsQuery = useQuery({
    queryKey: ['projects', { for: 'budget-tag' }],
    queryFn: () => fetchProjects({ limit: 200 }),
  });
  // FE-124 Sprint Q+: smart default — next quarter Jan-Mar / Apr-Jun / Jul-Sep / Oct-Dec
  // (бюджет планируется вперёд, не на текущий момент).
  const initialRange = nextQuarterRange();
  const [periodFrom, setPeriodFrom] = useState(initialRange.from);
  const [periodTo, setPeriodTo] = useState(initialRange.to);
  // Reference plusMonths to keep import live (used in some older logic paths).
  void plusMonths;
  const [currency, setCurrency] = useState<'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY'>('KZT');
  const [lines, setLines] = useState<LineForm[]>(
    mapping.map((r, i) => makeLineFromMapping(r.account_code, i)),
  );

  const createMut = useMutation({
    mutationFn: (body: BudgetCreateBody) => createBudget(body),
    onSuccess: (r) => void navigate({ to: '/budgets/$id', params: { id: r.id } }),
  });

  const totals = useMemo(() => {
    let revenue = new Decimal(0);
    let cost = new Decimal(0);
    for (const ln of lines) {
      const amt = new Decimal(ln.plan_amount || '0');
      if (ln.direction === 'revenue') revenue = revenue.plus(amt);
      else cost = cost.plus(amt);
    }
    return { revenue, cost, profit: revenue.minus(cost) };
  }, [lines]);

  const canNextStep1 = Boolean(name && periodFrom && periodTo);
  const canNextStep2 = lines.length > 0;

  const submit = () => {
    const body: BudgetCreateBody = {
      name_ru: name,
      project_tag: projectTag || null,
      period_from: periodFrom,
      period_to: periodTo,
      currency,
      lines: lines.map((l, i) => ({
        line_no: i + 1,
        account_code: l.account_code,
        account_name_ru: l.account_name_ru,
        direction: l.direction,
        plan_amount: { amount: l.plan_amount || '0', currency },
        period_split: l.period_split,
        notes_ru: null,
      })),
    };
    createMut.mutate(body);
  };

  const availableToAdd = mapping.filter(
    (r) => !lines.some((ln) => ln.account_code === r.account_code),
  );

  return (
    <div>
      <PageHeader title={m.budget_button_new()} />
      <StepIndicator current={step} total={3} />

      {step === 1 && (
        <div>
          <h2 className={shared.h2Sm}>{m.budget_column_name()}</h2>
          <div className={styles.formGrid2}>
            {/* FE-122 Sprint Q+: required `*` indicators. */}
            <Input
              label={m.budget_column_name()}
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {/* FE-125 Sprint Q+: datalist autocomplete для существующих project tags. */}
            <Input
              label="Проект (тег)"
              value={projectTag}
              onChange={(e) => setProjectTag(e.target.value)}
              list="budget-project-tags"
              placeholder="Опционально"
            />
            <datalist id="budget-project-tags">
              {(projectsQuery.data?.items ?? [])
                .filter((p) => !p.name.startsWith('[E2E-TEST]'))
                .map((p) => (
                  <option key={p.id} value={p.name} />
                ))}
            </datalist>
            <DatePicker
              label="Период с"
              required
              value={periodFrom || null}
              onChange={(next) => setPeriodFrom(next ?? '')}
            />
            <DatePicker
              label="Период по"
              required
              value={periodTo || null}
              onChange={(next) => setPeriodTo(next ?? '')}
            />
            <Select
              label={m.cp_column_currency()}
              required
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'KZT' | 'USD' | 'EUR' | 'RUB' | 'CNY')}
              options={[
                { value: 'KZT', label: 'KZT' },
                { value: 'USD', label: 'USD' },
                { value: 'EUR', label: 'EUR' },
                { value: 'RUB', label: 'RUB' },
                { value: 'CNY', label: 'CNY' },
              ]}
            />
          </div>
          {/* FE-123 Sprint Q+: sticky footer + Cancel button (вместо одинокого Next inline). */}
          <div className={shared.stickyFooter}>
            <Button type="button" variant="secondary" onClick={() => navigate({ to: '/budgets' })}>
              {m.common_button_cancel()}
            </Button>
            <Button variant="primary" onClick={() => setStep(2)} disabled={!canNextStep1}>
              {m.common_button_next()}
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2 className={shared.h2Sm}>{m.variance_column_plan()}</h2>
          {lines.map((ln, idx) => (
            <div key={ln.key} className={styles.lineRow}>
              <span className={styles.code}>{ln.account_code}</span>
              <span>{ln.account_name_ru}</span>
              <span>{ln.direction}</span>
              <Input
                label={m.variance_column_plan()}
                value={ln.plan_amount}
                onChange={(e) => {
                  const copy = [...lines];
                  copy[idx] = { ...copy[idx]!, plan_amount: e.target.value };
                  setLines(copy);
                }}
              />
              <Select
                label="Период"
                value={ln.period_split}
                onChange={(e) => {
                  const copy = [...lines];
                  copy[idx] = {
                    ...copy[idx]!,
                    period_split: e.target.value as 'quarterly' | 'monthly' | 'lump_sum',
                  };
                  setLines(copy);
                }}
                options={[
                  { value: 'quarterly', label: 'Квартально' },
                  { value: 'monthly', label: 'Ежемесячно' },
                  { value: 'lump_sum', label: 'Единовременно' },
                ]}
              />
              <Button
                variant="ghost"
                aria-label="Удалить строку"
                onClick={() => setLines(lines.filter((_, i) => i !== idx))}
              >
                <Trash2 size={16} />
              </Button>
            </div>
          ))}
          {availableToAdd.length > 0 && (
            <div className={styles.addRow}>
              <Select
                label="Добавить из маппинга"
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    setLines([...lines, makeLineFromMapping(e.target.value, lines.length)]);
                  }
                }}
                options={[
                  { value: '', label: '—' },
                  ...availableToAdd.map((r) => ({
                    value: r.account_code,
                    label: `${r.account_code} · ${r.account_name_ru}`,
                  })),
                ]}
              />
            </div>
          )}
          <div className={`${styles.actionsRow} ${shared.stickyFooter}`}>
            <Button variant="ghost" onClick={() => setStep(1)}>
              {m.common_button_back()}
            </Button>
            <Button variant="primary" onClick={() => setStep(3)} disabled={!canNextStep2}>
              {m.common_button_next()}
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2 className={shared.h2Sm}>{m.wizard_section_confirm()}</h2>
          <div className={styles.summaryCard}>
            <div>
              {m.budget_column_name()}: <strong>{name}</strong>
            </div>
            <div>
              Период:{' '}
              <strong>
                {periodFrom} — {periodTo}
              </strong>
            </div>
            <div>
              {m.wizard_summary_lines_count()} <strong>{lines.length}</strong>
            </div>
            <div className={styles.summaryGap}>
              {m.budget_column_revenue_plan()}:{' '}
              <strong className={shared.tabular}>
                {totals.revenue.toString()} {currency}
              </strong>
            </div>
            <div>
              {m.budget_column_cost_plan()}:{' '}
              <strong className={shared.tabular}>
                {totals.cost.toString()} {currency}
              </strong>
            </div>
            <div>
              {m.pnl_net_profit()}:{' '}
              <strong className={shared.tabular}>
                {totals.profit.toString()} {currency}
              </strong>
            </div>
          </div>
          <div className={`${styles.actionsRowFlat} ${shared.stickyFooter}`}>
            <Button variant="ghost" onClick={() => setStep(2)}>
              {m.common_button_back()}
            </Button>
            <Button variant="primary" onClick={submit} loading={createMut.isPending}>
              {m.common_button_create()}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
